<template>
  <v-row class="text-left full-height">
    <v-col cols="12">
      <DrawerToolbar
        title="Metric Summary"
        :admin="true"
        :showBackButton="showBackButton"
        :showCloseButton="!showBackButton"
        @back="onBack"
        @close="onBack"
      />

      <v-card-text class="py-6 px-4">
        <!-- Preview -->
        <div class="mb-2">
          <p class="subtitle-1 font-weight-bold mb-3">Preview</p>
          <MetricsSummaryBar
            :metrics="previewMockData"
            :metricsConfig="previewConfig"
            :previewMode="true"
          />
        </div>

        <!-- Metrics loop -->
        <div v-for="(metric, index) in metrics" :key="index">
          <v-divider class="my-6" />

          <div :data-testid="index === 3 ? 'metric-slot-3' : 'metric-slot'" class="metric-slot">
            <!-- Metric heading -->
            <p class="text-h6 mb-4">Metric {{ index + 1 }}</p>

            <!-- Select Metric Type -->
            <v-select
              v-model="metric.metricKey"
              :items="metricKeyOptions"
              item-text="text"
              item-value="value"
              label="Select Metric Type"
              outlined
              dense
              data-testid="metric-key-select"
              class="mb-2"
              @change="markDirty"
            />

            <!-- Card background section -->
            <p class="subtitle-1 font-weight-bold mb-3">Card background</p>

            <!-- Background color row -->
            <div class="d-flex align-center mb-2">
              <input
                type="color"
                v-model="metric.background.color"
                data-testid="primary-color-picker"
                aria-label="Background color picker"
                class="color-swatch"
                @input="markDirty"
              />
              <v-text-field
                v-model="metric.background.color"
                label="Primary color (in hexadecimal)"
                outlined
                dense
                :rules="hexColorRules"
                @input="markDirty"
              />
            </div>

            <!-- ADD GRADIENT button -->
            <Button
              v-if="!showGradient[index]"
              :dataTestId="'add-gradient-btn'"
              text
              iconLeft="mdi-plus"
              color="brandCyan"
              small
              class="mb-2 pl-0"
              @click="addGradient(index)"
              >ADD GRADIENT</Button
            >

            <!-- Secondary color row (gradient) -->
            <div
              v-if="showGradient[index]"
              data-testid="gradient-secondary-row"
              class="d-flex align-center mb-2"
            >
              <input
                type="color"
                v-model="metric.background.secondaryColor"
                data-testid="gradient-color-picker"
                aria-label="Gradient color picker"
                class="color-swatch"
                @input="markDirty"
              />
              <v-text-field
                v-model="metric.background.secondaryColor"
                label="Gradient color (in hexadecimal)"
                outlined
                dense
                :rules="hexColorRules"
                @input="markDirty"
              />
              <Button
                :dataTestId="'delete-gradient-btn'"
                icon
                aria-label="Remove gradient"
                @click="removeGradient(index)"
              >
                <Icon icon="mdi-delete" />
              </Button>
            </div>

            <!-- Text color section -->
            <p class="subtitle-1 font-weight-bold mb-3 mt-2">Text</p>

            <!-- Text color row -->
            <div class="d-flex align-center mb-2">
              <input
                type="color"
                v-model="metric.textColor"
                data-testid="text-color-picker"
                aria-label="Text color picker"
                class="color-swatch"
                @input="markDirty"
              />
              <v-text-field
                v-model="metric.textColor"
                label="Color (in hexadecimal)"
                outlined
                dense
                :rules="hexColorRules"
                @input="markDirty"
              />
            </div>

            <!-- Delete 4th metric -->
            <Button
              v-if="showFourthMetric && index === 3"
              :dataTestId="'delete-metric-btn-3'"
              icon
              aria-label="Remove 4th metric"
              @click="deleteMetric()"
            >
              <Icon icon="mdi-delete" />
            </Button>

            <!-- Validation error -->
            <div
              v-if="errors[index]"
              :data-testid="'validation-error-' + index"
              class="red--text caption"
            >
              {{ errors[index] }}
            </div>
          </div>
        </div>

        <!-- Divider + ADD METRIC button -->
        <v-divider class="my-6" />

        <Button
          v-if="!showFourthMetric"
          :dataTestId="'add-metric-btn'"
          text
          iconLeft="mdi-plus"
          color="brandCyan"
          small
          class="pl-0"
          @click="addMetric()"
          >ADD METRIC</Button
        >
      </v-card-text>

      <!-- Audience targeting -->
      <AudienceGroupSelector
        :clientId="globalClientId"
        :audienceGroups="audienceGroups"
        @update:audienceGroups="audienceGroups = $event"
        data-testid="audience-selector"
      />

      <!-- Bottom action buttons -->
      <div class="d-flex justify-end pa-4">
        <Button :dataTestId="'cancel-btn'" text color="brandPrimary" class="mr-4" @click="onCancel"
          >Cancel</Button
        >
        <Button
          :dataTestId="'save-btn'"
          color="brandPrimary"
          :loading="isLoading"
          :disabled="isLoading"
          @click="onSave"
        >
          Save
        </Button>
      </div>

      <div v-show="saveError" data-testid="save-error" class="red--text px-4 pb-2">
        {{ saveError }}
      </div>

      <!-- Unsaved changes modal -->
      <UnsavedChangesDialog
        v-model="showUnsavedModal"
        @stay="showUnsavedModal = false"
        @discard="discardAndClose"
      />
    </v-col>
  </v-row>
</template>

<script>
import DrawerToolbar from "@/components/generic/DrawerToolbar.vue";
import Button from "@/components/generic/Button.vue";
import Icon from "@/components/generic/Icon.vue";
import UnsavedChangesDialog from "@/components/generic/UnsavedChangesDialog.vue";
import MetricsSummaryBar from "@/components/participant/programs/MetricsSummaryBar.vue";
import ProgramService from "@/services/ProgramService";
import AudienceGroupSelector from "@/components/account-management/dashboard/AudienceGroupSelector.vue";
import { mapState } from "vuex";

const METRIC_KEY_OPTIONS = [
  { text: "Your Active Programs", value: "active-programs" },
  { text: "Points Earned This Month", value: "points-earned-month" },
  { text: "Points Earned This Year", value: "points-earned-year" },
  { text: "Programs Completed This Year", value: "programs-completed-year" },
];

const SAMPLE_VALUES = {
  "active-programs": "5",
  "points-earned-month": "549",
  "points-earned-year": "1,549",
  "programs-completed-year": "8",
};

const makeInitialMetrics = () => [
  { metricKey: null, background: { color: "#FFFFFF", gradient: null }, textColor: "#000000" },
  { metricKey: null, background: { color: "#FFFFFF", gradient: null }, textColor: "#000000" },
  { metricKey: null, background: { color: "#FFFFFF", gradient: null }, textColor: "#000000" },
];

function initialState() {
  return {
    isDirty: false,
    showUnsavedModal: false,
    metrics: makeInitialMetrics(),
    showGradient: [false, false, false],
    showFourthMetric: false,
    errors: {},
    isLoading: false,
    saveError: null,
    audienceGroups: null,
  };
}

export default {
  name: "MetricsSummaryCreator",

  components: {
    DrawerToolbar,
    Button,
    Icon,
    UnsavedChangesDialog,
    MetricsSummaryBar,
    AudienceGroupSelector,
  },

  props: {
    column: {
      type: Object,
      required: false,
      default: null,
    },
    rowId: {
      type: Number,
      required: true,
    },
    widgetTypeId: {
      type: Number,
      required: false,
      default: null,
    },
    initialAudienceGroups: { type: Array, default: null },
    showBackButton: { type: Boolean, default: true },
    globalDefaults: { type: Object, default: () => ({}) },
  },

  data() {
    return initialState();
  },

  computed: {
    ...mapState(["globalClientId"]),
    metricKeyOptions() {
      return METRIC_KEY_OPTIONS;
    },
    hexColorRules() {
      return [(v) => !v || /^#[0-9A-Fa-f]{6}$/.test(v) || "Enter a valid hex color (e.g. #FF0000)"];
    },
    previewMetrics() {
      const count = this.showFourthMetric ? 4 : 3;
      return this.metrics.slice(0, count).map((m) => {
        const option = METRIC_KEY_OPTIONS.find((o) => o.value === m.metricKey);
        return {
          label: option ? option.text : "—",
          value: m.metricKey ? SAMPLE_VALUES[m.metricKey] || "—" : "—",
          background: m.background.secondaryColor
            ? `linear-gradient(45deg, ${m.background.color} 1%, ${m.background.secondaryColor} 100%)`
            : m.background.color,
          textColor: m.textColor,
        };
      });
    },
    previewMockData() {
      return {
        activePrograms: 5,
        programsCompletedThisYear: 8,
        pointsEarnedThisMonth: 549,
        pointsEarnedThisYear: 1549,
      };
    },
    previewConfig() {
      const count = this.showFourthMetric ? 4 : 3;
      return this.metrics.slice(0, count).map((m) => ({
        metricKey: m.metricKey,
        background: {
          color: m.background.color,
          gradient: m.background.secondaryColor
            ? `linear-gradient(45deg, ${m.background.color} 1%, ${m.background.secondaryColor} 100%)`
            : null,
        },
        textColor: m.textColor,
      }));
    },
  },

  created() {
    this.audienceGroups =
      this.initialAudienceGroups && this.initialAudienceGroups.length
        ? this.initialAudienceGroups.map((g) => ({ ...g }))
        : null;
    if (this.column && this.column.config && this.column.config.metrics) {
      this.loadFromColumn();
    } else {
      const colId = this.column?.columnId ?? this.column?.dashboardColumnId;
      if (!colId) {
        // Truly new — apply global defaults
        if (this.globalDefaults?.cardBackgroundColor || this.globalDefaults?.textColor) {
          this.metrics.forEach((m) => {
            if (this.globalDefaults.cardBackgroundColor) {
              m.background.color = this.globalDefaults.cardBackgroundColor;
            }
            if (this.globalDefaults.textColor) {
              m.textColor = this.globalDefaults.textColor;
            }
          });
        }
      }
      // else: existing component with missing metrics config — use hardcoded defaults (already set by data())
    }
  },

  methods: {
    resetPage() {
      Object.assign(this.$data, initialState());
    },

    _parseSecondaryFromGradient(gradientStr) {
      const matches = gradientStr.match(/#[0-9A-Fa-f]{6}/g);
      return matches && matches.length >= 2 ? matches[1] : "#EEEEEE";
    },

    loadFromColumn() {
      const savedMetrics = this.column.config.metrics;

      this.metrics = savedMetrics.map((m) => ({
        metricKey: m.metricKey,
        background: {
          color: (m.background && m.background.color) || "#FFFFFF",
          gradient: (m.background && m.background.gradient) || null,
          ...(m.background?.secondaryColor
            ? { secondaryColor: m.background.secondaryColor }
            : m.background?.gradient?.startsWith?.("#")
            ? { secondaryColor: m.background.gradient }
            : m.background?.gradient
            ? { secondaryColor: this._parseSecondaryFromGradient(m.background.gradient) }
            : {}),
        },
        textColor: m.textColor || "#000000",
      }));

      if (savedMetrics.length === 4) {
        this.showFourthMetric = true;
      }

      const gradientFlags = savedMetrics.map(
        (m) => !!(m.background && (m.background.gradient || m.background.secondaryColor))
      );
      gradientFlags.forEach((val, i) => {
        this.$set(this.showGradient, i, val);
      });
    },

    markDirty() {
      this.isDirty = true;
      this.errors = {};
    },

    addGradient(index) {
      this.$set(this.metrics[index].background, "secondaryColor", "#EEEEEE");
      this.$set(this.showGradient, index, true);
      this.markDirty();
    },

    removeGradient(index) {
      this.$set(this.metrics[index].background, "secondaryColor", null);
      this.$set(this.metrics[index].background, "gradient", null);
      this.$set(this.showGradient, index, false);
      this.markDirty();
    },

    _requestClose() {
      if (this.isDirty) this.showUnsavedModal = true;
      else this.$emit("close");
    },

    onBack() {
      this._requestClose();
    },

    onCancel() {
      this._requestClose();
    },

    discardAndClose() {
      this.showUnsavedModal = false;
      this.$emit("close");
    },

    addMetric() {
      if (this.showFourthMetric) return;
      this.metrics.push({
        metricKey: null,
        background: { color: "#FFFFFF", gradient: null },
        textColor: "#000000",
      });
      this.$set(this.showGradient, 3, false);
      this.showFourthMetric = true;
      this.isDirty = true;
    },

    deleteMetric() {
      this.showFourthMetric = false;
      this.metrics.splice(3, 1);
      this.showGradient.splice(3, 1);
      this.isDirty = true;
    },

    validateBeforeSave() {
      this.errors = {};
      const visibleCount = this.showFourthMetric ? 4 : 3;
      const nonEmptyKeys = [];

      for (let i = 0; i < visibleCount; i++) {
        if (!this.metrics[i] || !this.metrics[i].metricKey) {
          this.$set(this.errors, i, "Please select a metric for each slot");
        } else {
          nonEmptyKeys.push({ index: i, key: this.metrics[i].metricKey });
        }
      }

      const keyCounts = {};
      nonEmptyKeys.forEach(({ key }) => {
        keyCounts[key] = (keyCounts[key] || 0) + 1;
      });
      nonEmptyKeys.forEach(({ index, key }) => {
        if (keyCounts[key] > 1) {
          this.$set(this.errors, index, "Each metric can only be selected once");
        }
      });

      return Object.keys(this.errors).length === 0;
    },

    buildConfig() {
      return {
        metrics: this.metrics.slice(0, this.showFourthMetric ? 4 : 3).map((m) => ({
          metricKey: m.metricKey,
          background: {
            color: m.background.color,
            gradient: m.background.secondaryColor
              ? `linear-gradient(45deg, ${m.background.color} 1%, ${m.background.secondaryColor} 100%)`
              : null,
            secondaryColor: m.background.secondaryColor || null,
          },
          textColor: m.textColor,
        })),
      };
    },

    async onSave() {
      if (Array.isArray(this.audienceGroups) && this.audienceGroups.length === 0) {
        return;
      }
      if (!this.validateBeforeSave()) return;

      this.saveError = null;
      this.isLoading = true;
      try {
        const config = this.buildConfig();

        const colId = this.column?.columnId ?? this.column?.dashboardColumnId;
        if (this.column && colId) {
          await ProgramService.updateDashboardColumn(colId, { config });
          this.$emit("saved", { columnId: colId, config, isNew: false });
        } else {
          const widgetTypeId = this.widgetTypeId;
          if (!widgetTypeId) {
            this.saveError = "Widget type is required";
            this.isLoading = false;
            return;
          }
          const response = await ProgramService.createDashboardColumn({
            rowId: this.rowId,
            widgetTypeId,
            position: 1,
            config,
            clientId: this.globalClientId,
          });
          const columnId = response?.data?.result?.columnId;
          this.$emit("saved", { columnId, config, isNew: true });
        }
        if (this.rowId) {
          await ProgramService.updateDashboardRow(this.rowId, {
            audienceGroups: this.audienceGroups,
          });
        }
        this.$emit("close");
      } catch (e) {
        this.saveError = e.message || "Failed to save";
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.color-swatch {
  width: 40px;
  height: 40px;
  min-width: 40px;
  margin-right: 8px;
  margin-bottom: 22px;
  cursor: pointer;
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 0;
  padding: 2px;
  appearance: none;
  -webkit-appearance: none;
}

.preview-card {
  min-height: 56px;
}
</style>
